import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Zap,
  Cpu,
  Eye,
  MessageSquare,
  Lock,
  Layers,
  ChevronRight,
  ChevronDown,
  Code2,
  Stethoscope,
  Search,
  CheckCircle2,
  Terminal,
  Play,
  Volume2,
  BrainCircuit,
  FileText,
  User,
  Users,
  Building2,
  Check,
  Plus,
  Minus,
  Quote
} from "lucide-react";
import TAELSAdaptiveSection from "./TAELSAdaptiveSection";
import VoiceRing from "./VoiceRing";

const Taels = () => {
  const navigate = useNavigate();

  // OpenAI Style "What can T.A.E.L.S do?" Feature Category Selector
  const [activeCapability, setActiveCapability] = useState("research");

  // Interactive Live Reasoning Terminal State (OpenAI Playground style)
  const [activeTab, setActiveTab] = useState("research");
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulatedStep, setSimulatedStep] = useState(0);

  // TAELS Animation Model Interactive State (VoiceRing)
  const [modelState, setModelState] = useState("idle");
  const [modelLevel, setModelLevel] = useState(null);
  const [ringSize, setRingSize] = useState(340);
  const modelTimeoutRef = useRef(null);
  const isInteractingRef = useRef(false);

  useEffect(() => {
    const updateSize = () => {
      if (typeof window !== "undefined") {
        if (window.innerWidth < 480) {
          setRingSize(290);
        } else if (window.innerWidth < 768) {
          setRingSize(330);
        } else {
          setRingSize(380);
        }
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Tapping/clicking triggers intelligent progression: thinking -> speaking -> idle
  const handleTriggerModelResponse = () => {
    if (modelTimeoutRef.current) clearTimeout(modelTimeoutRef.current);
    isInteractingRef.current = true;
    
    // 1) Transitions into thinking (bright orbital comet travels around the ring)
    setModelState("thinking");
    setModelLevel(null);

    // 2) Transitions into speaking (radiating pulse waves)
    modelTimeoutRef.current = setTimeout(() => {
      setModelState("speaking");
      setModelLevel(0.7);

      // 3) Settles smoothly back to idle
      modelTimeoutRef.current = setTimeout(() => {
        setModelState("idle");
        setModelLevel(null);
        isInteractingRef.current = false;
      }, 2800);
    }, 1800);
  };

  const handleMouseEnter = () => {
    if (isInteractingRef.current) return;
    setModelState("listening");
  };

  const handleMouseMove = (e) => {
    if (isInteractingRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const dist = Math.min(1, Math.sqrt(x * x + y * y) / (rect.width / 2));
    setModelLevel(0.25 + 0.65 * dist);
  };

  const handleMouseLeave = () => {
    if (isInteractingRef.current) return;
    setModelState("idle");
    setModelLevel(null);
  };

  const handleTouchStart = () => {
    if (isInteractingRef.current) return;
    setModelState("listening");
    setModelLevel(0.5);
  };

  const handleTouchMove = (e) => {
    if (isInteractingRef.current) return;
    if (e.touches && e.touches[0]) {
      const rect = e.currentTarget.getBoundingClientRect();
      const touch = e.touches[0];
      const x = touch.clientX - rect.left - rect.width / 2;
      const y = touch.clientY - rect.top - rect.height / 2;
      const dist = Math.min(1, Math.sqrt(x * x + y * y) / (rect.width / 2));
      setModelLevel(0.25 + 0.65 * dist);
    }
  };

  // FAQ Accordion State
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const promptDemos = {
    research: {
      category: "Deep Research & Reasoning",
      prompt: "Synthesize phase III oncology trial results and isolate multi-variable biomarkers with p < 0.01.",
      thinking: [
        "Analyzing 14,200 clinical vectors from trial cohorts…",
        "Validating statistical significance across genomic markers…",
        "Correlating EGFR kinase mutations with progression-free survival…",
        "Synthesis verified. Zero hallucination tolerance satisfied."
      ],
      result: "Isolated 3 primary biomarkers (EGFR-L858R, T790M, MET amplification) with p = 0.0034. Multi-modal patient risk profile generated with 99.4% confidence score."
    },
    code: {
      category: "Systems Architecture",
      prompt: "Optimize zero-copy IPC pipeline for distributed neural inference cluster with sub-millisecond p99 latency.",
      thinking: [
        "Inspecting memory allocation overhead in kernel ring buffers…",
        "Bypassing context switches via shared memory shm_open descriptor…",
        "Implementing lock-free ring buffer with atomic memory barriers…",
        "Throughput benchmarked at 4.2 million tensors/sec."
      ],
      result: "Reduced p99 IPC latency from 3.8ms to 0.42ms. Zero heap allocations during hot-path execution with SIMD-vectorized serialization."
    },
    multimodal: {
      category: "Vision & Spatial Reasoning",
      prompt: "Analyze the 3D surgical scan, segment vascular boundaries, and highlight arterial bifurcations.",
      thinking: [
        "Ingesting 512-slice volumetric DICOM tomography…",
        "Executing dense voxel semantic segmentation on mesenteric tree…",
        "Detecting 1.8mm arterial narrowing at superior mesenteric artery…",
        "Rendering precision spatial overlay."
      ],
      result: "Segmented 48 anatomical landmarks. Identified potential stenosis (72% lumen compromise) at coordinate (X: 142.4, Y: 89.1, Z: 312.0)."
    },
    stealth: {
      category: "Ghost Privacy Mask",
      prompt: "Execute enterprise competitive analysis under strict air-gapped cryptographic isolation.",
      thinking: [
        "Initializing local client-side memory enclave…",
        "Applying zero-knowledge homomorphic masking to prompt tokens…",
        "Routing through zero-log ephemeral execution channels…",
        "Wiping local inference traces from volatile buffer."
      ],
      result: "Complete strategic synthesis delivered. All intermediate prompt vectors, token sequences, and inference states securely flushed with zero cloud footprint."
    }
  };

  const currentDemo = promptDemos[activeTab];

  const runSimulation = () => {
    setIsSimulating(true);
    setSimulatedStep(0);
    const interval = setInterval(() => {
      setSimulatedStep((prev) => {
        if (prev >= currentDemo.thinking.length) {
          clearInterval(interval);
          setIsSimulating(false);
          return currentDemo.thinking.length;
        }
        return prev + 1;
      });
    }, 600);
  };

  // Apple-grade quintic cubic-bezier curve for fluid keynote transitions
  const appleEase = [0.16, 1, 0.3, 1];

  // Scroll animations for Hero
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const heroLogoScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const heroLogoOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.2]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const heroTextOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0.15]);

  // Meaning breakdown data for T.A.E.L.S
  const acronymBreakdown = [
    {
      letter: "T",
      word: "Truly",
      subtitle: "Uncompromising Integrity & Authenticity",
      description: "Not simulated or approximate intelligence. Grounded in formal verification, empirical proof, and zero-hallucination protocols so high-stakes decisions rest on indisputable logic.",
      icon: CheckCircle2,
      accent: "text-blue-400"
    },
    {
      letter: "A",
      word: "Adaptive",
      subtitle: "Dynamic Context Plasticity",
      description: "Fluidly calibrates its reasoning model to your specific domain vocabulary, complexity level, and intent. From medical oncology to distributed kernel architecture, it becomes your native partner.",
      icon: Zap,
      accent: "text-sky-400"
    },
    {
      letter: "E",
      word: "Efficient",
      subtitle: "Sub-120ms Latency & Optimized Compute",
      description: "Engineered with SIMD-vectorized execution, zero-copy memory ring buffers, and tiered thinking models. Achieve enterprise reasoning with minimum energy and instantaneous response times.",
      icon: Cpu,
      accent: "text-blue-500"
    },
    {
      letter: "L",
      word: "Learning",
      subtitle: "Continuous In-Session Evolution",
      description: "Synthesizes multi-turn context vectors and persistent thread memory without violating data boundaries. It grows more aligned with your thinking with every command issued.",
      icon: BrainCircuit,
      accent: "text-cyan-400"
    },
    {
      letter: "S",
      word: "Synthesis",
      subtitle: "Unified Multi-Modal Convergence",
      description: "Unites natural dialogue, vision parsing, real-time code execution, mathematical proofs, and live web exploration into one coherent, fluid cognitive stream.",
      icon: Layers,
      accent: "text-indigo-400"
    }
  ];

  // OpenAI Style "What can T.A.E.L.S do?" use-cases
  const capabilities = [
    {
      id: "research",
      title: "Research & Deep Analysis",
      icon: Search,
      headline: "Synthesize millions of sources with rigorous scientific accuracy.",
      detail: "T.A.E.L.S searches the live web, extracts structured data from multi-hundred-page PDFs, and evaluates conflicting hypotheses to produce boardroom-ready reports with verbatim citations.",
      tags: ["Deep Web Retrieval", "Financial Modeling", "Clinical Trial Meta-Analysis", "Document Vectorization"],
      sampleQuery: "Compare 5-year capital expenditures of hyperscale GPU clusters against distributed edge inference models."
    },
    {
      id: "code",
      title: "Code & Engineering",
      icon: Code2,
      headline: "Architect, write, review, and debug production code.",
      detail: "From low-level memory allocators in Rust to full-stack distributed React/Node pipelines, T.A.E.L.S generates tested, clean code, explains execution paths, and fixes race conditions in real time.",
      tags: ["Python Sandbox Execution", "Distributed Systems Design", "Automated Test Suites", "Security Auditing"],
      sampleQuery: "Write a high-throughput lock-free ring buffer in C++20 with cacheline padding and memory barriers."
    },
    {
      id: "multimodal",
      title: "Vision & Spatial Cognition",
      icon: Eye,
      headline: "See, analyze, and comprehend physical and technical imagery.",
      detail: "Upload circuit schematics, architectural blueprints, medical imaging, or UI mockups. T.A.E.L.S reads text in 90+ languages, segments visual components, and identifies microscopic anomalies.",
      tags: ["Schematic Deconstruction", "Medical Scan Spatial Overlay", "UI Code Generation", "Visual Anomaly Detection"],
      sampleQuery: "Identify potential single-points-of-failure in this high-availability electrical circuit diagram."
    },
    {
      id: "voice",
      title: "Real-Time Natural Voice",
      icon: Volume2,
      headline: "Fluid, human-like voice conversations with instantaneous response.",
      detail: "Talk to T.A.E.L.S naturally. Interrupt anytime, shift topics seamlessly, and practice presentations or brainstorm spontaneously with expressive, humanized speech synthesis.",
      tags: ["Sub-120ms Voice Turnaround", "Expressive Cadence", "Real-Time Interruption", "Hands-Free Productivity"],
      sampleQuery: "Let's roleplay a clinical advisory review on new cardiology guidelines. Challenge my recommendations."
    }
  ];

  // FAQ Data
  const faqs = [
    {
      q: "What does T.A.E.L.S stand for?",
      a: "T.A.E.L.S stands for Truly Adaptive, Efficient & Learning Synthesis. It is FOZZIL's proprietary cognitive intelligence architecture designed for high-consequence reasoning, deep research, and multi-modal task execution."
    },
    {
      q: "How does T.A.E.L.S protect user privacy and confidentiality?",
      a: "T.A.E.L.S operates under strict Zero-Knowledge principles. Your personal prompts, proprietary code, and sensitive documents are never used to train foundation models. In Blacksite mode, sessions run in ephemeral, air-gapped memory enclaves that are cryptographically purged after logout."
    },
    {
      q: "How does T.A.E.L.S differ from standard chat models?",
      a: "Standard models simply predict the most likely next token. T.A.E.L.S features a dedicated APEX Thinking Engine with deliberative psychological chain-of-thought analysis. It formulates hypotheses, executes counterfactual proofs, and validates citations before ever producing a response."
    },
    {
      q: "Can I use T.A.E.L.S for free?",
      a: "Yes. The T.A.E.L.S Pulse model is completely free to use. It provides fast conversational answers, web search integration, and voice interaction. Advanced thinking engines and enterprise Blacksite tiers are available for complex institutional workflows."
    },
    {
      q: "What tools can T.A.E.L.S execute autonomously?",
      a: "T.A.E.L.S can autonomously execute live Web Searches, run sandboxed Python scripts, inspect uploaded files (PDFs, images, spreadsheets), solve mathematical proofs, and generate creative visual imagery without requiring manual tool selection."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white selection:bg-blue-600/30 selection:text-white font-sans antialiased overflow-x-hidden">
      
      {/* Subtle Atmospheric FOZZIL Signature Glows */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-blue-600/[0.04] blur-[160px] rounded-full" />
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-blue-500/[0.03] blur-[140px] rounded-full" />
      </div>

      {/* ========================================================================= */}
      {/* 1. HERO SECTION: TAELS ANIMATION MODEL (Full Cover Screen)                 */}
      {/* ========================================================================= */}
      <section
        ref={heroRef}
        className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12 max-w-7xl mx-auto overflow-hidden"
      >
        {/* Centerpiece Visual: TAELS Animation Model */}
        <motion.div
          style={{ scale: heroLogoScale, opacity: heroLogoOpacity }}
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.1, ease: appleEase }}
          className="relative flex flex-col items-center justify-center select-none"
        >
          {/* Ambient Diffuse Aura */}
          <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/30 via-cyan-400/20 to-blue-500/30 blur-[120px] rounded-full pointer-events-none" />

          {/* Interactive TAELS Assistant Canvas (Clean User Interactive Animation) */}
          <div
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTriggerModelResponse}
            onClick={handleTriggerModelResponse}
            className="relative z-10 rounded-full overflow-hidden cursor-pointer p-0 transition-transform duration-500 hover:scale-[1.03] active:scale-[0.98] flex items-center justify-center shadow-[0_0_90px_rgba(6,93,192,0.4)]"
          >
            <VoiceRing size={ringSize} state={modelState} level={modelLevel} />
          </div>
        </motion.div>

        {/* Subtle Scroll Cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 hover:opacity-100 transition-opacity pointer-events-none"
        >
          <div className="w-4 h-7 rounded-full border border-white/20 flex items-start justify-center p-1">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-1.5 rounded-full bg-blue-400"
            />
          </div>
        </motion.div>
      </section>

      {/* ========================================================================= */}
      {/* 2. COMMAND T.A.E.L.S. KEYNOTE SECTION (Separate Full Cover Screen)        */}
      {/* ========================================================================= */}
      <section
        id="command-section"
        className="relative z-20 min-h-screen flex flex-col items-center justify-center px-6 py-16 max-w-7xl mx-auto border-t border-white/[0.08] text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.95, ease: appleEase }}
          className="flex flex-col items-center text-center space-y-6 sm:space-y-8 max-w-5xl"
        >
          {/* Main Headline */}
          <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-semibold tracking-tight text-white leading-[1.02]">
            Command. <br />
            <span className="bg-gradient-to-r from-blue-400 via-sky-300 to-blue-500 bg-clip-text text-transparent">
              T.A.E.L.S.
            </span>
          </h1>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 pt-3">
            <button
              onClick={() => window.open("https://taels.fozzil.in/app", "_blank", "noopener,noreferrer")}
              className="inline-flex items-center gap-2.5 px-8 sm:px-10 py-3.5 sm:py-4 rounded-full bg-white text-black font-semibold text-sm sm:text-base hover:bg-zinc-200 transition-all shadow-[0_0_40px_rgba(255,255,255,0.25)] hover:scale-[1.03] active:scale-[0.98] cursor-pointer"
            >
              <span>Talk to T.A.E.L.S</span>
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/pricing-taels")}
              className="text-sm sm:text-base text-[#2997ff] hover:text-sky-300 transition-colors inline-flex items-center gap-1.5 font-medium group cursor-pointer px-3 py-2"
            >
              <span>View T.A.E.L.S Plans</span>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </motion.div>

        {/* Discover Architecture Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35, duration: 0.8 }}
          className="flex flex-col items-center gap-2 hover:opacity-100 transition-opacity mt-14 sm:mt-20 cursor-pointer"
          onClick={() => {
            const arch = document.getElementById("architecture-section");
            if (arch) arch.scrollIntoView({ behavior: "smooth" });
          }}
        >
          <span className="text-[11px] sm:text-xs font-mono tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors">
            Discover Architecture
          </span>
          <div className="w-4 h-7 rounded-full border border-white/20 flex items-start justify-center p-1">
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-1.5 rounded-full bg-blue-400"
            />
          </div>
        </motion.div>
      </section>

      {/* ========================================================================= */}
      {/* 3. THE ACRONYM DECODED: TRULY · ADAPTIVE · EFFICIENT · LEARNING · SYNTHESIS */}
      {/* ========================================================================= */}
      <section
        id="architecture-section"
        className="relative z-20 min-h-screen flex flex-col justify-center py-20 md:py-28 px-6 max-w-7xl mx-auto border-t border-white/[0.08]"
      >
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85, ease: appleEase }}
          className="space-y-4 mb-16 max-w-3xl"
        >
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-blue-400">
            The Architecture Defined
          </div>
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white">
            What T.A.E.L.S stands for.
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 font-light leading-relaxed">
            Every letter represents a core engineering principle that separates T.A.E.L.S from superficial language wrappers.
          </p>
        </motion.div>

        {/* 5 Distinct Pillar Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-6">
          {acronymBreakdown.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 35, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: idx * 0.08, ease: appleEase }}
                className={`rounded-[22px] sm:rounded-[32px] bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/[0.08] p-4 sm:p-8 md:p-10 relative overflow-hidden group hover:border-blue-500/30 transition-all duration-500 flex flex-col justify-between ${
                  idx === 4 ? "col-span-2 lg:col-span-2" : ""
                }`}
              >
                <div className="space-y-4 sm:space-y-6">
                  {/* Top Letter & Icon */}
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/[0.03] border border-white/[0.08] flex items-center justify-center">
                      <span className={`text-lg sm:text-2xl font-bold font-mono ${item.accent}`}>
                        {item.letter}
                      </span>
                    </div>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/[0.02] border border-white/[0.05] flex items-center justify-center text-zinc-400 group-hover:text-white transition-colors">
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                  </div>

                  {/* Title & Meaning */}
                  <div className="space-y-1.5 sm:space-y-2">
                    <h3 className="text-lg sm:text-2xl md:text-3xl font-semibold text-white tracking-tight">
                      {item.word}
                    </h3>
                    <div className="text-[10px] sm:text-xs font-mono uppercase tracking-wider text-blue-400">
                      {item.subtitle}
                    </div>
                    <p className="text-xs sm:text-sm md:text-base text-zinc-400 font-light leading-relaxed pt-1 sm:pt-2">
                      {item.description}
                    </p>
                  </div>
                </div>

                <div className="pt-4 sm:pt-8 flex items-center gap-2 text-[10px] sm:text-xs font-mono text-zinc-500 group-hover:text-zinc-300 transition-colors">
                  <span>Pillar {idx + 1} of 5</span>
                  <div className="flex-1 h-px bg-white/[0.06]" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 2.5 ADAPTIVE COGNITION ("It doesn’t just answer. It evolves with you.")    */}
      {/* ========================================================================= */}
      <TAELSAdaptiveSection />

      {/* ========================================================================= */}
      {/* 4. "WHAT CAN T.A.E.L.S DO?" (OpenAI ChatGPT-Style Feature Breakdown)        */}
      {/* ========================================================================= */}
      <section className="relative z-20 min-h-screen flex flex-col justify-center pt-24 pb-20 md:pt-32 md:pb-28 px-6 bg-gradient-to-b from-black via-zinc-950/80 to-black border-t border-white/[0.08] scroll-mt-24">
        <div className="max-w-7xl mx-auto space-y-16">
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, ease: appleEase }}
            className="text-center space-y-4 max-w-3xl mx-auto"
          >
            <span className="text-xs font-mono text-blue-400 uppercase tracking-[0.25em]">Everyday & Advanced Powers</span>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-semibold text-white tracking-tight">
              What can you do with T.A.E.L.S?
            </h2>
            <p className="text-base sm:text-lg text-zinc-400 font-light leading-relaxed">
              From everyday questions to complex mathematical proofs, T.A.E.L.S assists across the entire spectrum of human intellect.
            </p>
          </motion.div>

          {/* Capabilities Showcase Tabs (Strictly listed in ONE LINE on desktop/tablet, horizontal scroll on mobile) */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.75, delay: 0.1, ease: appleEase }}
            className="w-full max-w-5xl mx-auto flex items-center flex-nowrap justify-start lg:justify-center gap-2 sm:gap-3 p-1.5 bg-white/[0.03] border border-white/[0.08] rounded-full overflow-x-auto no-scrollbar"
          >
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              const isSelected = activeCapability === cap.id;
              return (
                <button
                  key={cap.id}
                  onClick={() => setActiveCapability(cap.id)}
                  className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                    isSelected
                      ? "bg-white text-black font-semibold shadow-xl"
                      : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{cap.title}</span>
                </button>
              );
            })}
          </motion.div>

          {/* Active Capability Showcase Card (OpenAI Style Deep-Dive) */}
          {(() => {
            const cap = capabilities.find((c) => c.id === activeCapability) || capabilities[0];
            return (
              <motion.div
                key={cap.id}
                initial={{ opacity: 0, y: 30, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: 0.15, ease: appleEase }}
                className="rounded-[36px] bg-gradient-to-br from-white/[0.06] via-white/[0.02] to-transparent border border-white/[0.1] p-8 sm:p-14 relative overflow-hidden"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                  <div className="space-y-6">
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-400">
                      {cap.title}
                    </div>

                    <h3 className="text-2xl sm:text-4xl font-semibold text-white tracking-tight leading-tight">
                      {cap.headline}
                    </h3>

                    <p className="text-zinc-300 font-light leading-relaxed text-sm sm:text-base">
                      {cap.detail}
                    </p>

                    <div className="flex flex-wrap gap-2 pt-2">
                      {cap.tags.map((tag, i) => (
                        <span key={i} className="text-xs font-mono text-zinc-300 bg-white/[0.04] border border-white/[0.08] px-3.5 py-1.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Right: Simulated Command Card */}
                  <div className="bg-[#05070d] border border-white/[0.1] rounded-[28px] p-6 sm:p-8 space-y-4 font-mono shadow-2xl">
                    <div className="text-xs text-zinc-500 flex items-center justify-between border-b border-white/[0.08] pb-3">
                      <span>EXAMPLE WORKFLOW</span>
                      <span className="text-blue-400">T.A.E.L.S APEX Ready</span>
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs text-zinc-400">PROMPT:</div>
                      <div className="text-white text-sm bg-white/[0.04] p-3.5 rounded-xl border border-white/[0.06]">
                        "{cap.sampleQuery}"
                      </div>
                    </div>
                    <div className="pt-2">
                      <button
                        onClick={() => window.open(`https://taels.fozzil.in/app?q=${encodeURIComponent(cap.sampleQuery)}`, "_blank", "noopener,noreferrer")}
                        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs transition-all shadow-lg cursor-pointer"
                      >
                        <span>Try this prompt in T.A.E.L.S</span>
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })()}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 5. INTERACTIVE REASONING SIMULATOR (OpenAI Playground Style)               */}
      {/* ========================================================================= */}
      <section className="relative z-20 min-h-screen flex flex-col justify-center py-20 md:py-28 px-6 max-w-5xl mx-auto space-y-10">
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85, ease: appleEase }}
          className="text-center space-y-3 max-w-2xl mx-auto"
        >
          <span className="text-xs font-mono text-blue-400 uppercase tracking-[0.25em]">Interactive Proof</span>
          <h2 className="text-3xl sm:text-5xl font-semibold text-white tracking-tight">
            Watch T.A.E.L.S reason in real time.
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 font-light">
            Select a domain workflow to observe the cognitive deliberation process before final output delivery.
          </p>
        </motion.div>

        {/* Workflow Tabs (Strictly listed in ONE LINE on desktop/tablet, smooth horizontal scroll on mobile) */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: appleEase }}
          className="w-full max-w-4xl mx-auto flex items-center flex-nowrap justify-start sm:justify-center gap-2 p-1.5 bg-white/[0.04] border border-white/[0.08] rounded-full overflow-x-auto no-scrollbar"
        >
          {[
            { id: "research", label: "Clinical Research", icon: Stethoscope },
            { id: "code", label: "Systems Architecture", icon: Code2 },
            { id: "multimodal", label: "Spatial Diagnostics", icon: Eye },
            { id: "stealth", label: "Stealth Enclave", icon: ShieldCheck }
          ].map((tab) => {
            const Icon = tab.icon;
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setIsSimulating(false);
                  setSimulatedStep(0);
                }}
                className={`flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                  isSelected
                    ? "bg-white text-black font-semibold shadow-lg"
                    : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </motion.div>

        {/* Terminal Box */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-70px" }}
          transition={{ duration: 0.85, delay: 0.15, ease: appleEase }}
          className="rounded-[28px] sm:rounded-[36px] bg-[#05070d] border border-white/[0.1] shadow-[0_30px_100px_rgba(0,0,0,0.8)] overflow-hidden"
        >
          {/* Terminal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] bg-white/[0.02]">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="ml-3 text-xs font-mono text-zinc-500 tracking-wider">
                taels-apex-core // {activeTab}.session
              </span>
            </div>
            <button
              onClick={runSimulation}
              disabled={isSimulating}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition-all disabled:opacity-50 cursor-pointer shadow-[0_0_20px_rgba(37,99,235,0.4)]"
            >
              <Play className="w-3 h-3 fill-current" />
              <span>{isSimulating ? "Reasoning…" : "Execute Reasoning"}</span>
            </button>
          </div>

          {/* Terminal Body */}
          <div className="p-6 sm:p-8 space-y-6 font-mono text-xs sm:text-sm">
            <div className="space-y-1.5">
              <div className="text-zinc-500 text-xs">USER COMMAND:</div>
              <div className="text-white bg-white/[0.04] p-3.5 rounded-xl border border-white/[0.06]">
                {currentDemo.prompt}
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <div className="text-blue-400 text-xs font-semibold flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                <span>COGNITIVE DELIBERATION STREAM</span>
              </div>
              <div className="space-y-2 pl-3 border-l-2 border-blue-500/30">
                {currentDemo.thinking.slice(0, isSimulating ? simulatedStep : currentDemo.thinking.length).map((step, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-zinc-400 flex items-center gap-2"
                  >
                    <span className="text-blue-500/80">▸</span>
                    <span>{step}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {(!isSimulating || simulatedStep >= currentDemo.thinking.length) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-1.5 pt-3 border-t border-white/[0.06]"
              >
                <div className="text-emerald-400 text-xs font-semibold">SYNTHESIZED RESULT:</div>
                <div className="text-zinc-200 leading-relaxed bg-emerald-500/[0.04] p-4 rounded-xl border border-emerald-500/20">
                  {currentDemo.result}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </section>

      {/* ========================================================================= */}
      {/* 6. AUDIENCE / WHO IS IT FOR (Individuals, Teams, Enterprise)              */}
      {/* ========================================================================= */}
      <section className="relative z-20 min-h-screen flex flex-col justify-center py-20 md:py-28 px-6 max-w-7xl mx-auto border-t border-white/[0.08]">
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85, ease: appleEase }}
          className="space-y-4 mb-16 max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-blue-400">
            <Users className="w-3.5 h-3.5" />
            <span>Deployment Versatility</span>
          </div>
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white">
            Built for individuals. Scaled for enterprise.
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5 sm:gap-6">
          {[
            {
              title: "For Individuals",
              role: "Everyday Super-Intelligence",
              desc: "A responsive intellectual companion in your pocket. Clarify complex ideas, draft writings, study exams, or practice speeches with intuitive voice conversation.",
              icon: User,
              cta: "Get Started Free",
              action: () => window.open("https://taels.fozzil.in/app", "_blank")
            },
            {
              title: "For Builders & Engineers",
              role: "Architectural Workspaces",
              desc: "Deep code review, Python runtime execution, git diff generation, and technical documentation synthesis. Connect your terminal workflows directly to T.A.E.L.S.",
              icon: Code2,
              cta: "Launch Workspace",
              action: () => window.open("https://taels.fozzil.in/app", "_blank")
            },
            {
              title: "For Healthcare & Enterprise",
              role: "Zero-Knowledge Air-Gapped",
              desc: "HIPAA & SOC2 compliant private deployments. Dedicated compute enclaves, client-side encryption, and strict zero-training guarantees on proprietary intellectual property.",
              icon: Building2,
              cta: "Explore Blacksite",
              action: () => navigate("/pricing-taels")
            }
          ].map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 35, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: idx * 0.1, ease: appleEase }}
                className={`rounded-[22px] sm:rounded-[32px] bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/[0.08] p-4 sm:p-8 md:p-10 flex flex-col justify-between hover:border-blue-500/30 transition-all duration-500 ${
                  idx === 2 ? "col-span-2 md:col-span-1" : ""
                }`}
              >
                <div className="space-y-4 sm:space-y-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-blue-400">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    <span className="text-[10px] sm:text-xs font-mono text-zinc-500 uppercase tracking-widest">{item.role}</span>
                    <h3 className="text-lg sm:text-2xl font-bold text-white tracking-tight">{item.title}</h3>
                    <p className="text-zinc-400 font-light text-xs sm:text-sm md:text-base leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>

                <div className="pt-5 sm:pt-8">
                  <button
                    onClick={item.action}
                    className="w-full py-2.5 sm:py-3 rounded-full bg-white/[0.05] hover:bg-white/[0.1] text-white border border-white/[0.1] font-semibold text-[10px] sm:text-xs tracking-wider uppercase transition-all cursor-pointer"
                  >
                    {item.cta}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 7. TRUST, SAFETY & PRIVACY (VARAEN & S.H.A.D.E.)                          */}
      {/* ========================================================================= */}
      <section className="relative z-20 min-h-screen flex flex-col justify-center py-20 md:py-28 px-6 bg-gradient-to-b from-black via-zinc-950/80 to-black border-t border-white/[0.08] overflow-hidden">
        {/* Ambient atmospheric lighting */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[550px] bg-[radial-gradient(ellipse_70%_50%_at_50%_50%,rgba(6,93,192,0.15),transparent_70%)] blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto space-y-12 sm:space-y-16">
          
          {/* Dual Official Security Badges */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: appleEase }}
            className="flex items-center justify-center gap-5 sm:gap-6"
          >
            {/* Official VARAEN Metallic Logo */}
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl overflow-hidden border border-[#065DC0]/40 shadow-2xl shadow-[#065DC0]/30 bg-[#07090e]">
              <img src="/assets/varaen-logo.jpg" alt="FOZZIL VARAEN" className="w-full h-full object-cover block" />
            </div>

            <div className="w-[1px] h-10 sm:h-12 bg-white/10" />

            {/* Official S.H.A.D.E. Engine Logo */}
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02] flex items-center justify-center p-2.5 sm:p-3">
              <img src="/S.H.A.D.E logo-wobg.png" alt="S.H.A.D.E. Defense Engine" className="w-full h-full object-contain" />
            </div>
          </motion.div>

          {/* Heading and Slogan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, delay: 0.1, ease: appleEase }}
            className="text-center space-y-4 max-w-3xl mx-auto"
          >
            <span className="text-xs font-mono text-emerald-400 uppercase tracking-[0.25em]">
              VARAEN & S.H.A.D.E. · Uncompromising Standards
            </span>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-semibold text-white tracking-tight leading-tight">
              Your Intelligence. <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-indigo-300">
                Unseen. Untouched.
              </span>
            </h2>
            <p className="text-base sm:text-lg text-zinc-400 font-light leading-relaxed">
              Privacy is not an added feature — it is our foundational architecture. All T.A.E.L.S models, sessions, and APIs are protected by <strong>VARAEN</strong>, with <strong>S.H.A.D.E.</strong> autonomously isolating hostile threats and injection attempts in real time.
            </p>
          </motion.div>

          {/* S.H.A.D.E. Spotlight Banner */}
          <motion.div
            initial={{ opacity: 0, y: 35, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, delay: 0.15, ease: appleEase }}
            className="p-6 sm:p-8 rounded-[24px] sm:rounded-[32px] bg-[#070b14] border border-[#065DC0]/30 flex flex-col sm:flex-row items-center gap-6 text-left shadow-[0_20px_50px_rgba(6,93,192,0.15)] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 blur-[100px] pointer-events-none rounded-full" />
            <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-2xl bg-[#065DC0]/15 border border-[#065DC0]/30 flex items-center justify-center p-3 relative z-10">
              <img src="/S.H.A.D.E logo-wobg.png" alt="S.H.A.D.E. Defense Engine" className="w-full h-full object-contain" />
            </div>
            <div className="flex-1 text-center sm:text-left relative z-10 space-y-1.5">
              <div className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-blue-400">
                Autonomous Threat Isolation · Built into T.A.E.L.S
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                S.H.A.D.E. (Secure Hostile Adaptive Defence Engine)
              </h3>
              <p className="text-xs sm:text-sm text-zinc-300 font-light leading-relaxed">
                Operating directly inside VARAEN, S.H.A.D.E. continuously inspects incoming tokens, quarantines adversarial prompt injection attacks, and enforces zero-knowledge memory air-gapping without human latency.
              </p>
            </div>
          </motion.div>

          {/* 3 Privacy & Security Cards (2-per-row on mobile) */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5 sm:gap-6 md:gap-8">
            {[
              {
                title: "Zero-Knowledge Compute",
                desc: "Plaintext clinical and personal records are encrypted client-side. Even FOZZIL infrastructure cannot inspect your private data.",
                icon: Lock
              },
              {
                title: "S.H.A.D.E. Threat Defense",
                desc: "Autonomous threat neutralization operating inside VARAEN. Suspicious injection attacks and unauthorized tokens are quarantined in milliseconds.",
                icon: ShieldCheck
              },
              {
                title: "Zero Model Training",
                desc: "Your queries, codebases, documents, and medical data are never used to train or refine public foundation models. Period.",
                icon: CheckCircle2
              }
            ].map((safety, idx) => {
              const Icon = safety.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30, scale: 0.98 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.65, delay: idx * 0.1, ease: appleEase }}
                  className={`p-4 sm:p-8 rounded-[20px] sm:rounded-[32px] bg-white/[0.02] border border-white/[0.06] space-y-3 sm:space-y-4 hover:border-blue-500/30 transition-colors ${
                    idx === 2 ? "col-span-2 md:col-span-1" : ""
                  }`}
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <h3 className="text-base sm:text-xl font-semibold text-white tracking-tight">{safety.title}</h3>
                  <p className="text-xs sm:text-sm text-zinc-400 font-light leading-relaxed">{safety.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Privacy Link (Opens in new tab always) */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.25, ease: appleEase }}
            className="pt-2 text-center"
          >
            <a
              href="/security-and-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#2997ff] hover:text-sky-300 font-medium transition-colors cursor-pointer group"
            >
              <span>Explore Privacy & Security Architecture at FOZZIL</span>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 8. FREQUENTLY ASKED QUESTIONS (Apple/OpenAI Interactive Accordion)         */}
      {/* ========================================================================= */}
      <section className="relative z-20 min-h-screen flex flex-col justify-center py-20 md:py-28 px-6 max-w-4xl mx-auto border-t border-white/[0.08]">
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85, ease: appleEase }}
          className="space-y-4 mb-16 text-center"
        >
          <span className="text-xs font-mono text-blue-400 uppercase tracking-[0.25em]">Answers & Specifications</span>
          <h2 className="text-3xl sm:text-5xl font-semibold text-white tracking-tight">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: idx * 0.06, ease: appleEase }}
                className="rounded-[24px] bg-white/[0.03] border border-white/[0.06] overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex items-center justify-between p-6 sm:p-7 text-left text-white font-medium text-base sm:text-lg cursor-pointer hover:bg-white/[0.02]"
                >
                  <span>{faq.q}</span>
                  <div className="w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center text-zinc-400 shrink-0 ml-4">
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 sm:px-7 pb-6 text-sm sm:text-base text-zinc-400 font-light leading-relaxed border-t border-white/[0.04] pt-4">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 9. APPLE-STYLE GRAND FINALE CTA                                            */}
      {/* ========================================================================= */}
      <section className="relative z-20 min-h-screen flex flex-col items-center justify-center py-20 md:py-28 px-6 border-t border-white/[0.08] text-center overflow-hidden">
        {/* Subtle Atmospheric Floor Glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-blue-600/[0.05] blur-[150px] rounded-full pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: appleEase }}
          className="relative z-10 max-w-4xl mx-auto space-y-8"
        >
          <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-black border border-white/20 shadow-2xl p-0">
            <img
              src="/TAELS Logo black bg.png"
              alt="TAELS"
              className="w-full h-full object-cover rounded-full"
            />
          </div>

          <h2 className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight text-white leading-[1.05]">
            Your intelligence advantage <br />
            begins today.
          </h2>

          <p className="text-base sm:text-xl text-zinc-400 font-light max-w-xl mx-auto leading-relaxed">
            Experience the next era of autonomous cognitive computing. Zero telemetry. Absolute precision.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
            <button
              onClick={() => window.open("https://taels.fozzil.in/app", "_blank", "noopener,noreferrer")}
              className="inline-flex items-center gap-2.5 px-9 py-4 rounded-full bg-white text-black font-semibold text-base hover:bg-zinc-200 transition-all shadow-[0_0_50px_rgba(255,255,255,0.25)] hover:scale-[1.03] active:scale-[0.98] cursor-pointer"
            >
              <span>Launch T.A.E.L.S</span>
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/pricing-taels")}
              className="text-sm sm:text-base text-[#2997ff] hover:text-sky-300 transition-colors inline-flex items-center gap-1.5 font-medium group cursor-pointer px-2 py-1"
            >
              <span>Explore All Plans</span>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="pt-12 text-xs font-mono text-zinc-600 flex items-center justify-center gap-6">
            <span>S.H.A.D.E</span>
            <span>•</span>
            <span>Zero Data Scraping</span>
            <span>•</span>
            <span>Continuous Adaptive Audits</span>
          </div>
        </motion.div>
      </section>

    </div>
  );
};

export default Taels;
